import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const NoTracks = ({ summary: t = {} }) => {
  const reasons = [];
  if (!t.total_tracks) reasons.push("You haven't uploaded any tracks to your RGM profile yet.");
  if (t.in_review) reasons.push(`${t.in_review} track${t.in_review === 1 ? " is" : "s are"} still waiting for RGM review. Only released tracks can enter.`);
  if (t.not_released) reasons.push(`${t.not_released} track${t.not_released === 1 ? " was" : "s were"} not approved for release, so ${t.not_released === 1 ? "it" : "they"} can't be entered.`);
  if (t.missing_audio) reasons.push(`${t.missing_audio} released track${t.missing_audio === 1 ? " has" : "s have"} no playable audio file. Re-upload the audio to use ${t.missing_audio === 1 ? "it" : "them"}.`);
  if (t.hidden) reasons.push(`${t.hidden} released track${t.hidden === 1 ? " is" : "s are"} hidden from your profile.`);
  return (
    <div className="mt-2 text-sm text-gray-300 space-y-1" data-testid="fanvote-no-tracks">
      <p className="font-semibold text-white">None of your tracks can be entered yet.</p>
      <ul className="text-gray-400 space-y-0.5" data-testid="fanvote-no-tracks-reasons">{reasons.map((r) => <li key={r}>· {r}</li>)}</ul>
      <p className="text-xs text-gray-500">To qualify, a track must be yours, released (approved) on RGM, visible, and have an audio file. <Link to="/submit" className="underline text-purple-300" data-testid="fanvote-upload-track-link">Upload a track</Link></p>
    </div>
  );
};

export const EnterPanel = ({ round, spotsLeft, onEntered }) => {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState("");
  const [authed, setAuthed] = useState(true);
  useEffect(() => {
    api.get(`/fan-vote/rounds/${round.round_id}/my-entry`).then((r) => setData(r.data)).catch(() => setAuthed(false));
  }, [round.round_id]);
  const submit = async () => {
    try { await api.post(`/fan-vote/rounds/${round.round_id}/entries`, { submission_id: pick }); toast.success("Song submitted for review"); onEntered(); setData({ ...data, entry: { status: "pending" } }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
  };
  if (!["upcoming", "open"].includes(round.phase)) return null;
  return (
    <Card className="bg-black/40 border border-purple-500/30 p-5" data-testid="fanvote-enter-panel">
      <h3 className="text-white font-bold">Artists: enter one song</h3>
      {!authed ? (
        <p className="text-sm text-gray-400 mt-2"><Link to={`/login?next=/fan-vote`} className="underline text-purple-300" data-testid="fanvote-enter-login">Sign in</Link> to enter a song from your RGM profile.</p>
      ) : !data ? <p className="text-sm text-gray-500 mt-2">Loading…</p> : data.entry ? (
        <p className="text-sm text-gray-300 mt-2" data-testid="fanvote-my-entry-status">Your entry is <b>{data.entry.status}</b>.{data.entry.status === "approved" && data.entry.entry_id ? <> Share your <Link className="underline text-purple-300" to={`/fan-vote/entry/${data.entry.entry_id}`}>voting link</Link>.</> : ""}</p>
      ) : spotsLeft <= 0 ? <p className="text-sm text-gray-400 mt-2" data-testid="fanvote-round-full">This round is full.</p>
        : data.eligible_tracks.length === 0 ? <NoTracks summary={data.track_summary} /> : (
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 bg-gray-900 border border-white/10 rounded-md text-white text-sm px-3 h-10" data-testid="fanvote-track-select">
              <option value="">Choose a track…</option>
              {data.eligible_tracks.map((t) => <option key={t.submission_id} value={t.submission_id}>{t.track_title}</option>)}
            </select>
            <Button disabled={!pick} onClick={submit} className="bg-purple-600 hover:bg-purple-700" data-testid="fanvote-submit-entry-btn">Enter song</Button>
          </div>
        )}
      <p className="text-[11px] text-gray-500 mt-2">{spotsLeft} spot{spotsLeft === 1 ? "" : "s"} left · entries are reviewed by RGM before they appear.</p>
    </Card>
  );
};
